import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { OPERATOR_LABELS } from '@/utils/filters/filterConstants';

/**
 * FilterTagsBar Component
 * Displays active filters, sorts, and date ranges as interactive chips below the toolbar.
 */
const FilterTagsBar = ({
    rules = [],
    filters = [],
    filterRules = [],
    onRemoveRule,
    onRemoveFilter,
    onRemove,
    onFilterChange,
    onFilterRulesChange,
    onAddFilter,
    onClearAll,
    properties = [],
    dateRange = { start: null, end: null },
    onRemoveDateRange,
    onDateRangeClear,
    compact = false,
    sortRules = [],
    onRemoveSort,
    onPillClick,
}) => {
    // Standardize inputs
    let activeRules = [];
    if (Array.isArray(rules) && rules.length > 0) activeRules = rules;
    else if (Array.isArray(filters) && filters.length > 0) activeRules = filters;
    else if (Array.isArray(filterRules) && filterRules.length > 0) activeRules = filterRules;

    const removeRule = onRemoveRule || onRemoveFilter || onRemove;
    const removeDate = onRemoveDateRange || onDateRangeClear;
    const hasDate = dateRange?.start || dateRange?.end;

    if (activeRules.length === 0 && !hasDate && sortRules.length === 0) return null;

    const getPropId = (item) => item.propertyId || item.fieldId || item.field || item.property;

    const getPropertyLabel = (id) => {
        if (!id) return '';
        const prop = properties.find(p => p.id === id);
        return prop?.label || id.charAt(0).toUpperCase() + id.slice(1).replace(/([A-Z])/g, ' $1');
    };

    const getRuleDisplayValue = (rule) => {
        const rawOp = rule.operator || 'is';
        const op = OPERATOR_LABELS[rawOp] || rawOp.replace(/_/g, ' ');
        const val = rule.value;
        if (val === '' || val === undefined || val === null) return op;
        if (Array.isArray(val)) return `${op} ${val.join(', ')}`;
        // Handle date objects like { date: '2026-05-19', endDate: '...' }
        if (typeof val === 'object' && val !== null) {
            if (val.date && val.endDate) {
                return `${op} ${new Date(val.date).toLocaleDateString()} – ${new Date(val.endDate).toLocaleDateString()}`;
            }
            if (val.date) {
                return `${op} ${new Date(val.date).toLocaleDateString()}`;
            }
            // For relative dates like { unit: 'days', amount: 7 }
            if (val.unit && val.amount) {
                return `${op} last ${val.amount} ${val.unit}`;
            }
            // Fallback: just show operator
            return op;
        }
        return `${op} ${val}`;
    };

    const handleRemove = (rule, idx) => {
        if (removeRule) removeRule(idx);
        else if (onFilterChange) onFilterChange(activeRules.filter((_, i) => i !== idx));
        else if (onFilterRulesChange) onFilterRulesChange(activeRules.filter((_, i) => i !== idx));
    };

    return (
        <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '6px',
            alignItems: 'center', flex: 1, minWidth: 0
        }}>
            <AnimatePresence mode="popLayout">
                {/* Date range pill */}
                {hasDate && (
                    <motion.div
                        key="date-range" layout
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: 'transparent', border: '1px solid var(--border)',
                            borderRadius: '6px', padding: '4px 10px', height: '24px',
                            fontSize: '11.5px', color: 'var(--text-secondary)'
                        }}
                    >
                        <ObliqueIcon name="calendar_today" size={12} color="var(--accent)" />
                        <span style={{ fontWeight: '500' }}>Date Range</span>
                        <span style={{ opacity: 0.3 }}>:</span>
                        <span style={{ fontWeight: '400' }}>
                            {dateRange.start && dateRange.end
                                ? `${new Date(dateRange.start).toLocaleDateString()} – ${new Date(dateRange.end).toLocaleDateString()}`
                                : dateRange.start
                                    ? `From ${new Date(dateRange.start).toLocaleDateString()}`
                                    : `Until ${new Date(dateRange.end).toLocaleDateString()}`
                            }
                        </span>
                        {removeDate && (
                            <button onClick={removeDate} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', borderRadius: '50%' }} className="hover-bg-subtle">
                                <ObliqueIcon name="close" size={14} />
                            </button>
                        )}
                    </motion.div>
                )}

                {/* Sort pills */}
                {sortRules.map((sort, idx) => (
                    <motion.div
                        key={`sort-${getPropId(sort)}-${idx}`} layout
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: 'transparent', border: '1px solid var(--border)',
                            borderRadius: '6px', padding: '4px 10px', height: '24px',
                            fontSize: '11.5px', color: 'var(--text-secondary)'
                        }}
                    >
                        <ObliqueIcon name="swap_vert" size={12} color="var(--text-tertiary)" />
                        <span style={{ fontWeight: '500' }}>{getPropertyLabel(getPropId(sort))}</span>
                        <span style={{ opacity: 0.3 }}>:</span>
                        <span>{sort.direction === 'desc' ? 'Descending' : 'Ascending'}</span>
                        {onRemoveSort && (
                            <button onClick={() => onRemoveSort(idx)} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', borderRadius: '50%' }} className="hover-bg-subtle">
                                <ObliqueIcon name="close" size={14} />
                            </button>
                        )}
                    </motion.div>
                ))}

                {/* Filter pills */}
                {activeRules.map((rule, idx) => (
                    <motion.div
                        key={`${getPropId(rule)}-${idx}`} layout
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        onClick={() => onPillClick?.(rule, idx)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: 'transparent', border: '1px solid var(--border)',
                            borderRadius: '6px', padding: '4px 10px', height: '24px',
                            fontSize: '11.5px', color: 'var(--text-secondary)',
                            cursor: onPillClick ? 'pointer' : 'default',
                            transition: 'border-color 0.15s ease'
                        }}
                    >
                        <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>
                            {getPropertyLabel(getPropId(rule))}
                        </span>
                        <span style={{ opacity: 0.3 }}>:</span>
                        <span style={{ fontWeight: '400' }}>{getRuleDisplayValue(rule)}</span>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleRemove(rule, idx); }}
                            style={{
                                background: 'transparent', border: 'none', color: 'var(--text-tertiary)',
                                cursor: 'pointer', padding: '2px', marginLeft: '2px',
                                display: 'flex', alignItems: 'center', borderRadius: '50%'
                            }}
                            className="hover-bg-subtle"
                        >
                            <ObliqueIcon name="close" size={14} />
                        </button>
                    </motion.div>
                ))}

                {/* + Add filter button */}
                {onAddFilter && (
                    <motion.button
                        layout
                        whileHover={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
                        onClick={onAddFilter}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: 'transparent', border: '1px dashed var(--border)',
                            borderRadius: '6px', padding: '4px 10px', height: '24px',
                            fontSize: '11.5px', color: 'var(--text-tertiary)',
                            cursor: 'pointer', transition: 'all 0.15s'
                        }}
                    >
                        <ObliqueIcon name="add" size={14} />
                        Add filter
                    </motion.button>
                )}

                {/* Clear all */}
                {onClearAll && (
                    <motion.button
                        layout
                        whileHover={{ color: 'var(--danger)' }}
                        onClick={onClearAll}
                        style={{
                            background: 'transparent', border: 'none',
                            color: 'var(--text-tertiary)', fontSize: '11.5px',
                            cursor: 'pointer', padding: '4px 6px',
                            borderRadius: '4px', transition: 'color 0.15s'
                        }}
                    >
                        Clear all
                    </motion.button>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FilterTagsBar;
